import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Search, Hammer } from "lucide-react";
import { BuMappingDialog } from "@/components/operations/BuMappingDialog";

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");
const fmtMoney = (v) => (v || v === 0 ? `$${Number(v).toLocaleString("en-AU", { maximumFractionDigits: 0 })}` : "—");
const statusTone = (s) => ({
  IN_PROGRESS: "bg-sky-100 text-sky-800",
  NOT_STARTED: "bg-slate-100 text-slate-600",
  PENDING: "bg-amber-100 text-amber-800",
  ON_HOLD: "bg-rose-100 text-rose-700",
  ASSIGNED: "bg-violet-100 text-violet-800",
  SCHEDULED: "bg-emerald-100 text-emerald-800",
}[s] || "bg-slate-100 text-slate-600");

export const WipPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [buFilter, setBuFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 400);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const { data: d } = await api.get("/operations/wip", {
          params: {
            ...(buFilter ? { bu: buFilter } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(qDebounced ? { q: qDebounced } : {}),
          },
        });
        setData(d);
      } catch (e) { /* toast handled globally on 401 */ }
      finally { setBusy(false); }
    })();
  }, [buFilter, statusFilter, qDebounced, refreshKey]);

  if (!data) return <div className="p-10 text-center text-sm text-slate-400"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading WIP…</div>;

  return (
    <div className="space-y-3" data-testid="ops-wip-panel">
      {/* BU filter chips */}
      <div className="flex flex-wrap gap-1.5" data-testid="wip-bu-chips">
        <button onClick={() => setBuFilter("")} data-testid="wip-bu-all"
                className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                  !buFilter ? "bg-gbe text-white border-gbe" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
          All BUs <span className="opacity-70">({data.total})</span>
          {data.total_overdue > 0 && <span className={`ml-1 ${!buFilter ? "text-rose-200" : "text-rose-600"}`}>· {data.total_overdue} overdue</span>}
        </button>
        {(data.by_bu || []).map((b) => (
          <button key={b.business_unit} onClick={() => setBuFilter(buFilter === b.business_unit ? "" : b.business_unit)}
                  data-testid={`wip-bu-${b.business_unit.split(" ")[0]}`}
                  className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                    buFilter === b.business_unit ? "bg-gbe text-white border-gbe" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {b.business_unit} <span className="opacity-70">({b.n})</span>
            {b.overdue > 0 && <span className={`ml-1 ${buFilter === b.business_unit ? "text-rose-200" : "text-rose-600"}`}>· {b.overdue}</span>}
          </button>
        ))}
      </div>

      {/* Uninvoiced WIP value by BU */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto" data-testid="wip-value-summary">
        <div className="px-3 py-2 border-b border-slate-100 text-xs font-semibold text-slate-800">
          WIP value by business unit <span className="font-normal text-slate-400">— work done to date net of progress invoices claimed (ex GST, from AroFlo)</span>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-3 py-1.5">Business unit</th>
              <th className="text-right font-medium px-3 py-1.5">Open jobs</th>
              <th className="text-right font-medium px-3 py-1.5">Sell to date</th>
              <th className="text-right font-medium px-3 py-1.5">Invoiced to date</th>
              <th className="text-right font-medium px-3 py-1.5">Remaining WIP</th>
              <th className="text-right font-medium px-3 py-1.5">Cost</th>
              <th className="text-right font-medium px-3 py-1.5">Margin</th>
              <th className="text-right font-medium px-3 py-1.5">Labour (hrs)</th>
              <th className="text-right font-medium px-3 py-1.5">Materials</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(data.by_bu || []).map((b) => (
              <tr key={b.business_unit} className={`hover:bg-slate-50/60 ${buFilter === b.business_unit ? "bg-amber-50/50" : ""}`}
                  data-testid={`wip-value-${b.business_unit.split(" ")[0]}`}>
                <td className="px-3 py-1.5 text-slate-700">{b.business_unit}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{b.n}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtMoney(b.sell)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{fmtMoney(b.invoiced)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold text-slate-900">{fmtMoney(b.remaining)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtMoney(b.cost)}</td>
                <td className={`px-3 py-1.5 text-right tabular-nums ${b.margin_pct != null && b.margin_pct < 0 ? "text-rose-600 font-semibold" : "text-slate-600"}`}>
                  {b.margin_pct != null ? `${b.margin_pct}%` : "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{b.labour_hours}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">{fmtMoney(b.materials)}</td>
              </tr>
            ))}
            {data.totals && (
              <tr className="bg-slate-50 font-semibold text-slate-800" data-testid="wip-value-total">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right tabular-nums">{data.total}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(data.totals.sell)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(data.totals.invoiced)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(data.totals.remaining)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(data.totals.cost)}</td>
                <td className={`px-3 py-2 text-right tabular-nums ${data.totals.margin_pct != null && data.totals.margin_pct < 0 ? "text-rose-600" : ""}`}>
                  {data.totals.margin_pct != null ? `${data.totals.margin_pct}%` : "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{data.totals.labour_hours}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(data.totals.materials)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Status + search */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="wip-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All open statuses</SelectItem>
            {(data.statuses || []).map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")} ({(data.by_status || []).find((x) => x.status === s)?.n || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job #, title, client, technician…"
                 className="h-9 pl-8 text-xs" data-testid="wip-search" />
        </div>
        <BuMappingDialog onChanged={() => { setBuFilter(""); setRefreshKey((k) => k + 1); }} />
        <span className="text-[11px] text-slate-400 ml-auto">
          {busy ? <Loader2 className="w-3.5 h-3.5 inline animate-spin" /> : `${data.items.length} shown${data.items.length >= 200 ? " (oldest due first, capped 200)" : ""}`}
        </span>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-medium px-3 py-2">Job #</th>
              <th className="text-left font-medium px-3 py-2">Title</th>
              <th className="text-left font-medium px-3 py-2">Client</th>
              <th className="text-left font-medium px-3 py-2">Business unit</th>
              <th className="text-left font-medium px-3 py-2">Status</th>
              <th className="text-left font-medium px-3 py-2">Technician</th>
              <th className="text-right font-medium px-3 py-2">Sell</th>
              <th className="text-right font-medium px-3 py-2">Invoiced</th>
              <th className="text-right font-medium px-3 py-2">Remaining</th>
              <th className="text-right font-medium px-3 py-2">Due</th>
              <th className="text-right font-medium px-3 py-2">Days open</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.length === 0 && (
              <tr><td colSpan={10} className="px-3 py-10 text-center text-slate-400" data-testid="wip-empty">
                <Hammer className="w-5 h-5 mx-auto mb-2 opacity-40" />No work in progress matches these filters.</td></tr>
            )}
            {data.items.map((it) => (
              <tr key={it.id} className="hover:bg-slate-50/60" data-testid={`wip-row-${it.job_number || it.id}`}>
                <td className="px-3 py-2 font-mono text-slate-700 whitespace-nowrap">{it.job_number || "—"}</td>
                <td className="px-3 py-2 text-slate-800 max-w-[280px] truncate" title={it.title || ""}>{it.title || "—"}</td>
                <td className="px-3 py-2 text-slate-600 max-w-[180px] truncate" title={it.client_name || ""}>{it.client_name || "—"}</td>
                <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{it.business_unit}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <Badge className={`${statusTone(it.status)} font-normal`}>{(it.status || "").replace(/_/g, " ")}</Badge>
                  {it.substatus && <span className="block text-[10px] text-slate-400 mt-0.5">{it.substatus}</span>}
                </td>
                <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{it.technician || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-600 whitespace-nowrap">{fmtMoney(it.sell)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500 whitespace-nowrap">
                  {fmtMoney(it.invoiced_amt)}{it.invoiced_amt > 0 && <span className="block text-[9px] text-sky-600 uppercase">progress claimed</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800 whitespace-nowrap">{fmtMoney(it.remaining)}</td>
                <td className={`px-3 py-2 text-right whitespace-nowrap tabular-nums ${it.overdue ? "text-rose-600 font-semibold" : "text-slate-600"}`}>
                  {fmtDate(it.due_date)}{it.overdue && <span className="block text-[9px] uppercase">overdue</span>}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{it.days_open ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
